import {
  emptyConfig,
  NEXTFLOW_CONFIG,
  SCRIPT_PIPE_FOOTER,
  SCRIPT_PIPE_HEADER,
} from "@via/components/tree-structure/helpers/constants.tsx";
import {
  type AdvancedFilesInterface,
  type TreeData,
  type TreeDataScriptInterface,
} from "@via/utils/interfaces.tsx";

function findOrCreateFolder(parent, name, idCounter): TreeData {
  let folder = parent.find(item => item.name === name);
  if (!folder) {
    folder = {
      id: (idCounter.value++).toString(),
      name,
      children: [],
      path: name,
    };
    parent.push(folder);
  }
  return folder;
}

function addFileToStructure(structure, file, idCounter): void {
  const parts = file.filename.split("/");
  const fileName = parts.pop();
  let currentLevel = structure;

  // Iterate through the parts to find/create the necessary folders
  parts.forEach(part => {
    currentLevel = part
      ? findOrCreateFolder(currentLevel, part, idCounter).children
      : [];
  });

  // Add the file to the final directory
  if (fileName) {
    currentLevel.push({
      id: (idCounter.value++).toString(),
      name: fileName,
      path: file.filename,
      text: file.text,
    });
  }
}

export const createTreeStructure = (files): TreeData[] => {
  const idCounter = { value: 0 }; // Use an object to keep a reference to the counter
  return files.reduce((acc, file) => {
    addFileToStructure(acc, file, idCounter);
    return acc;
  }, []);
};

export const createPath = (lineage, name): string => {
  const path: string[] = [name]; // Initialize path with the provided 'name'
  let parent = lineage;

  // Loop to traverse up the parent lineage
  while (parent) {
    // Add the parent's name to the path if it exists
    if (parent.data?.name) {
      path.push(parent.data.name);
    }
    // Move up to the next parent
    parent = parent.parent;
  }

  // Reverse to make the path from the eldest parent down to the name provided
  return path.reverse().join("/");
};

export function updateChildPath(node, path): TreeDataScriptInterface {
  // Function to update children paths recursively
  const updatePaths = (item, newPath): void => {
    // Update the path only if currentObj is not the top-level object
    if (item !== node) {
      item.path = newPath + (item.name ? "/" + item.name : "");
    }

    if (item.children && Array.isArray(item.children)) {
      item.children.forEach(child => {
        updatePaths(child, item.path || newPath);
      });
    }
  };

  // Set the top-level path correctly
  node.path = path;

  // Start the recursive update from the top-level object
  updatePaths(node, path);
  return node;
}

// We are decoding escape code as strings below to
// render the file editor in "Advanced" tab under "Pipelines"

// [{"filename":"nextflow.config", "text":editorScriptPipeConfig}]
// \n//~@:~\n@~:"filename"\n//~@:~\ntext
export const createMultiConfig = (data): AdvancedFilesInterface[] => {
  const {
    scriptPipeConfig,
    scriptPipeHeader,
    scriptPipeFooter,
    extractedPipeScript,
  } = data;

  // Initialize the result array with header and footer, conditionally included
  const result: AdvancedFilesInterface[] = [
    {
      filename: SCRIPT_PIPE_HEADER,
      text: scriptPipeHeader ?? "",
    },
    {
      filename: SCRIPT_PIPE_FOOTER,
      text: scriptPipeFooter ?? "",
    },
  ];

  const rawConfig = extractedPipeScript ?? scriptPipeConfig;

  if (!rawConfig) {
    // If config is falsy, include an empty config at the start
    result.unshift(emptyConfig);
  } else {
    // Decode the HTML entities in the config
    const decodedConfig = rawConfig;
    const separator = "\n//~@:~\n";
    const configItems = decodedConfig.split(separator);
    // Process each config item
    for (let i = 0; i < configItems.length; i++) {
      const match = configItems[i].match(/@~:"(.*)"/);
      if (match) {
        const filename = match[1];
        const text = configItems[i + 1];
        if (filename && text !== undefined) {
          // Insert before header and footer
          result.splice(result.length - 2, 0, { filename, text });
        }
      }
    }
    // A config that was not written by the editor (git or file import, API)
    // is a plain string with no wrapper. Open it as the default config file
    // so a Save keeps its text instead of writing the config back empty.
    if (result.length === 2) {
      result.unshift({ filename: NEXTFLOW_CONFIG, text: rawConfig });
    }
  }
  return result;
};

export const update = (config, file): AdvancedFilesInterface[] => {
  return config.map(item => (item?.filename === file?.filename ? file : item));
};

export const preserveTree = (nodes): any => {
  return nodes.reduce((acc, cur) => {
    if (cur.isInternal && cur.isOpen) {
      acc[cur.id.toString()] = true;
    }
    return acc;
  }, {});
};
